// Utility functions for managing exam status in database

import {
  createUserExamProgress,
  deleteUserExamProgress,
  getUserExamProgress,
  getAllUserExamProgress,
  cleanupExpiredUserExamProgress,
  cleanupUserExamProgress,
} from "../services/UserExamProgressService";

// Key structure: exam_status_{examId}_{assessmentId}
const getExamStatusKey = (examId, assessmentId) => {
  return `exam_status_${examId}_${assessmentId}`;
};

// Save exam status when user starts an exam
export const saveExamStatus = async (examId, assessmentId, examData) => {
  try {
    const statusKey = getExamStatusKey(examId, assessmentId);
    // localStorage.setItem(statusKey, JSON.stringify(examData));
    await createUserExamProgress(statusKey, {
      examData,
    });
  } catch (error) {
    console.error("Error saving exam status:", error);
  }
};

// Get exam status from localStorage
export const getExamStatus = async (examId, assessmentId) => {
  try {
    const statusKey = getExamStatusKey(examId, assessmentId);
    // const savedStatus = localStorage.getItem(statusKey);

    // if (savedStatus) {
    //   const status = JSON.parse(savedStatus);
    //   return status;
    // }
    const response = await getUserExamProgress(statusKey);
    return response?.data?.examData;
  } catch (error) {
    console.error("Error getting exam status:", error);
    return null;
  }
};

// Update last accessed time
export const updateExamLastAccessed = async (examId, assessmentId) => {
  try {
    const status = await getExamStatus(examId, assessmentId);
    if (status) {
      status.lastAccessed = Date.now();
      const statusKey = getExamStatusKey(examId, assessmentId);
      // localStorage.setItem(statusKey, JSON.stringify(status));
      await createUserExamProgress(statusKey, {
        examData: status,
      });
    }
  } catch (error) {
    console.error("Error updating exam last accessed:", error);
  }
};

// Remove exam status (when completed or expired)
export const removeExamStatus = async (examId, assessmentId) => {
  try {
    const statusKey = getExamStatusKey(examId, assessmentId);
    // localStorage.removeItem(statusKey);
    await deleteUserExamProgress(statusKey);
  } catch (error) {
    console.error("Error removing exam status:", error);
  }
};

// Get all active exam statuses for current user
export const getAllActiveExamStatuses = async () => {
  try {
    const response = await getAllUserExamProgress();
    return response?.data || [];
  } catch (error) {
    console.error("Error getting all active exam statuses:", error);
    return [];
  }
};

// Check if exam status is still valid
const checkExamStatusValidity = (status) => {
  if (!status.isActive) {
    return false;
  }

  // Check if exam end time has passed
  if (status.examEndTime && Date.now() > status.examEndTime) {
    return false;
  }

  // Check if exam duration has passed
  if (status.examDuration > 0) {
    const elapsedMinutes = (Date.now() - status.startTime) / (1000 * 60);
    if (elapsedMinutes > status.examDuration) {
      return false;
    }
  }

  return true;
};

// Check if user has any unfinished exams for a specific assessment
export const getUnfinishedExamForAssessment = async (assessmentId) => {
  try {
    const activeExams = await getAllActiveExamStatuses();
    return activeExams.find((exam) => exam.assessmentId === assessmentId);
  } catch (error) {
    console.error("Error getting unfinished exam for assessment:", error);
    return null;
  }
};

// Format remaining time for display
export const formatRemainingTime = (status) => {
  // Prefer absolute end time if present
  if (status.examEndTime) {
    const remainingMs = Math.max(0, status.examEndTime - Date.now());
    const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = Math.max(0, remainingMinutes % 60);

    if (hours > 0) {
      return `${hours} giờ ${minutes} phút`;
    } else {
      return `${minutes} phút`;
    }
  }

  if (!status.examDuration || !status.startTime) return null;

  const elapsedMs = Date.now() - status.startTime;
  const elapsedMinutes = elapsedMs / (1000 * 60);
  const remainingMinutes = Math.max(0, status.examDuration - elapsedMinutes);

  const hours = Math.floor(remainingMinutes / 60);
  const minutes = Math.floor(remainingMinutes % 60);

  if (hours > 0) {
    return `${hours} giờ ${minutes} phút`;
  } else {
    return `${minutes} phút`;
  }
};

// Check if there are any unfinished exams globally
export const hasAnyUnfinishedExams = async () => {
  try {
    const activeExams = await getAllActiveExamStatuses();
    return activeExams.length > 0;
  } catch (error) {
    console.error("Error checking for unfinished exams:", error);
    return false;
  }
};

// Get the most recent unfinished exam
export const getMostRecentUnfinishedExam = async () => {
  try {
    const activeExams = await getAllActiveExamStatuses();
    if (activeExams.length === 0) return null;

    // Sort by lastAccessed time, most recent first
    activeExams.sort((a, b) => b.lastAccessed - a.lastAccessed);
    return activeExams[0];
  } catch (error) {
    console.error("Error getting most recent unfinished exam:", error);
    return null;
  }
};

// Clean up expired exam statuses
// export const cleanupExpiredExamStatuses = async () => {
//   try {
//     await cleanupExpiredUserExamProgress();
//   } catch (error) {
//     console.error("Error during cleanup:", error);
//   }
// };

// Clean up exam statuses for specific assessment
export const cleanupExamStatusesForAssessment = async (assessmentId) => {
  try {
    const response = await cleanupUserExamProgress(assessmentId);
    return response?.data;
  } catch (error) {
    console.error("Error cleaning up exam statuses for assessment:", error);
    throw error;
  }
};
